// TrialReminderStep.tsx
// Step 3: Trial timeline and reminder selection (only shown if trial exists)

import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable } from 'react-native';
import { Text, Button } from '../../../components/ui';
import { Theme } from '../../../utils/theme';
import { PaywallHeader } from '../components/PaywallHeader';
import { ChatAvatar } from '../../../components/ui';
import type { PaywallStepProps, ReminderChoice } from '../types';
import { SubscriptionService } from '../../../services/subscriptionService';

export const TrialReminderStep: React.FC<PaywallStepProps> = ({
  selection,
  onUpdateSelection,
  onNext,
  onClose,
}) => {
  const [selectedReminder, setSelectedReminder] = useState<ReminderChoice | null>(
    selection.reminderDays || null
  );

  // Get actual trial days from the selected package
  const trialDays = selection.package
    ? SubscriptionService.getTrialDays(selection.package) || 7
    : 7;

  const handleContinue = () => {
    if (selectedReminder) {
      onUpdateSelection({ reminderDays: selectedReminder });
      onNext();
    }
  };

  return (
    <View style={styles.container}>
      <PaywallHeader onClose={onClose} />
      
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero section with mentor avatar */}
        <View style={styles.hero}>
          <View style={styles.avatarContainer}>
            <ChatAvatar type="mentor" size="large" />
          </View>
          
          <Text variant="h2" style={styles.title}>
            Try Mentorist{'\n'}for {trialDays} Days Free
          </Text>
          <Text variant="body1" style={styles.subtitle}>
            We'll remind you before your trial ends. Cancel anytime—no questions, no fees.
          </Text>
        </View>

        {/* When to remind section */}
        <Text variant="body1" style={styles.sectionTitle}>
          When should we remind you?
        </Text>
        <View style={styles.reminderOptions}>
          <Pressable
            style={[
              styles.reminderOption,
              selectedReminder === 2 && styles.reminderOptionSelected,
            ]}
            onPress={() => setSelectedReminder(2)}
          >
            <View style={styles.reminderContent}>
              <View style={styles.dateCircle}>
                <Text variant="h3" style={styles.dateText}>
                  {new Date(Date.now() + (trialDays - 2) * 24 * 60 * 60 * 1000).getDate()}
                </Text>
              </View>
              <View style={{ flex: 1, marginLeft: Theme.spacing.md }}>
                <Text
                  variant="body1"
                  style={[
                    styles.reminderText,
                    selectedReminder === 2 && styles.reminderTextSelected,
                  ]}
                >
                  2 days before
                </Text>
                <Text
                  variant="body2"
                  style={[
                    styles.reminderSubtext,
                    selectedReminder === 2 && styles.reminderSubtextSelected,
                  ]}
                >
                  {new Date(Date.now() + (trialDays - 2) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Text>
              </View>
            </View>
            <View style={[styles.radio, selectedReminder === 2 && styles.radioSelected]}>
              {selectedReminder === 2 && <View style={styles.radioDot} />}
            </View>
          </Pressable>

          <Pressable
            style={[
              styles.reminderOption,
              selectedReminder === 3 && styles.reminderOptionSelected,
            ]}
            onPress={() => setSelectedReminder(3)}
          >
            <View style={styles.reminderContent}>
              <View style={styles.dateCircle}>
                <Text variant="h3" style={styles.dateText}>
                  {new Date(Date.now() + (trialDays - 3) * 24 * 60 * 60 * 1000).getDate()}
                </Text>
              </View>
              <View style={{ flex: 1, marginLeft: Theme.spacing.md }}>
                <Text
                  variant="body1"
                  style={[
                    styles.reminderText,
                    selectedReminder === 3 && styles.reminderTextSelected,
                  ]}
                >
                  3 days before
                </Text>
                <Text
                  variant="body2"
                  style={[
                    styles.reminderSubtext,
                    selectedReminder === 3 && styles.reminderSubtextSelected,
                  ]}
                >
                  {new Date(Date.now() + (trialDays - 3) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Text>
              </View>
            </View>
            <View style={[styles.radio, selectedReminder === 3 && styles.radioSelected]}>
              {selectedReminder === 3 && <View style={styles.radioDot} />}
            </View>
          </Pressable>
        </View>

        {/* Trust message */}
        <View style={styles.trustBox}>
          <Text variant="body2" style={styles.trustText}>
            💡 Easy to cancel in just 15 seconds. No penalties or fees.
          </Text>
        </View>

        {/* CTA */}
        <Button
          title="Continue"
          onPress={handleContinue}
          disabled={!selectedReminder}
          style={styles.ctaButton}
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 80,
    paddingHorizontal: Theme.spacing.xxl,
    paddingBottom: Theme.spacing.xxxl,
  },
  hero: {
    alignItems: 'center',
    marginBottom: Theme.spacing.xl,
    backgroundColor: '#E8F9F3', // Mint green like onboarding
    paddingVertical: Theme.spacing.xxxl,
    paddingHorizontal: Theme.spacing.xl,
    borderRadius: Theme.borderRadius.xl,
    marginHorizontal: -Theme.spacing.xxl,
    marginTop: -Theme.spacing.lg,
    ...Theme.shadows.md,
  },
  avatarContainer: {
    marginBottom: Theme.spacing.lg,
  },
  title: {
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  subtitle: {
    textAlign: 'center',
    color: Theme.colors.textSecondary,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: Theme.spacing.md,
    color: Theme.colors.textPrimary,
  },
  reminderOptions: {
    marginBottom: Theme.spacing.lg,
  },
  reminderOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    borderWidth: 2,
    borderColor: Theme.colors.border,
    marginBottom: Theme.spacing.md,
  },
  reminderOptionSelected: {
    borderColor: Theme.colors.primary,
    backgroundColor: Theme.colors.primaryLight,
  },
  reminderContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: Theme.colors.surfaceSecondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateText: {
    color: Theme.colors.textPrimary,
    fontWeight: '600',
  },
  reminderText: {
    fontWeight: '600',
    marginBottom: 2,
  },
  reminderTextSelected: {
    color: Theme.colors.primary,
  },
  reminderSubtext: {
    color: Theme.colors.textSecondary,
    fontSize: 12,
  },
  reminderSubtextSelected: {
    color: Theme.colors.primary,
  },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioSelected: {
    borderColor: Theme.colors.primary,
  },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Theme.colors.primary,
  },
  trustBox: {
    backgroundColor: Theme.colors.warm50,
    borderRadius: Theme.borderRadius.md,
    padding: Theme.spacing.md,
    marginBottom: Theme.spacing.xl,
    borderWidth: 1,
    borderColor: Theme.colors.warm200,
  },
  trustText: {
    textAlign: 'center',
    color: Theme.colors.textSecondary,
  },
  ctaButton: {
    marginBottom: Theme.spacing.lg,
  },
});

